import type { CellValue } from 'exceljs';

export const XLSX_MIME = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';

/** Mehr Zeilen/Spalten rendert die Tabelle nicht; der Rest steht im Download. */
export const SPREADSHEET_PREVIEW_MAX_ROWS = 2_000;
export const SPREADSHEET_PREVIEW_MAX_COLS = 100;

export type SpreadsheetPreviewKind = 'xlsx' | 'csv';

export type SpreadsheetSheet = {
  name: string;
  hidden: boolean;
  /** Zeilen mit Excel-Zeilennummer; leere Zeilen fehlen. */
  rows: Array<{ rowNumber: number; cells: string[] }>;
  columnCount: number;
  /** Zeilen mit Inhalt insgesamt, auch über SPREADSHEET_PREVIEW_MAX_ROWS hinaus. */
  totalRows: number;
};

/** Nur .xlsx und .csv; alte .xls/.ods kann der Browser nicht lesen. */
export function spreadsheetPreviewKind(
  filename: string | null | undefined,
  mimeType?: string | null,
): SpreadsheetPreviewKind | null {
  const name = String(filename ?? '').toLowerCase();
  const mime = String(mimeType ?? '').split(';')[0].trim().toLowerCase();
  if (name.endsWith('.xlsx') || mime === XLSX_MIME) return 'xlsx';
  if (name.endsWith('.csv') || mime === 'text/csv') return 'csv';
  return null;
}

export function columnLetter(index: number): string {
  let n = index + 1;
  let out = '';
  while (n > 0) {
    const rem = (n - 1) % 26;
    out = String.fromCharCode(65 + rem) + out;
    n = Math.floor((n - 1) / 26);
  }
  return out;
}

/** Excel speichert CSV unter Windows meist in Windows-1252. */
export function decodeCsvBytes(bytes: ArrayBuffer): string {
  try {
    return new TextDecoder('utf-8', { fatal: true }).decode(bytes);
  } catch {
    return new TextDecoder('windows-1252').decode(bytes);
  }
}

function detectDelimiter(text: string): string {
  const firstLine = text.split(/\r?\n/, 1)[0] ?? '';
  const candidates = [';', ',', '\t'];
  let best = ';';
  let bestCount = -1;
  for (const c of candidates) {
    const count = firstLine.split(c).length - 1;
    if (count > bestCount) {
      best = c;
      bestCount = count;
    }
  }
  return best;
}

/** RFC-4180-CSV mit Anführungszeichen; Trennzeichen aus der ersten Zeile. */
export function parseCsv(text: string): string[][] {
  const source = text.replace(/^﻿/, '');
  const delimiter = detectDelimiter(source);
  const rows: string[][] = [];
  let row: string[] = [];
  let field = '';
  let quoted = false;
  for (let i = 0; i < source.length; i += 1) {
    const ch = source[i];
    if (quoted) {
      if (ch === '"') {
        if (source[i + 1] === '"') {
          field += '"';
          i += 1;
        } else {
          quoted = false;
        }
      } else {
        field += ch;
      }
      continue;
    }
    if (ch === '"' && field === '') {
      quoted = true;
    } else if (ch === delimiter) {
      row.push(field);
      field = '';
    } else if (ch === '\n' || ch === '\r') {
      if (ch === '\r' && source[i + 1] === '\n') i += 1;
      row.push(field);
      rows.push(row);
      row = [];
      field = '';
    } else {
      field += ch;
    }
  }
  if (field !== '' || row.length > 0) {
    row.push(field);
    rows.push(row);
  }
  return rows;
}

function trimTrailingEmpty(cells: string[]): string[] {
  let end = cells.length;
  while (end > 0 && cells[end - 1] === '') end -= 1;
  return cells.slice(0, end);
}

function buildSheet(name: string, hidden: boolean, source: Array<{ rowNumber: number; cells: string[] }>): SpreadsheetSheet {
  const rows: SpreadsheetSheet['rows'] = [];
  let columnCount = 0;
  let totalRows = 0;
  for (const row of source) {
    const cells = trimTrailingEmpty(row.cells);
    if (cells.length === 0) continue;
    totalRows += 1;
    if (rows.length >= SPREADSHEET_PREVIEW_MAX_ROWS) continue;
    const shown = cells.slice(0, SPREADSHEET_PREVIEW_MAX_COLS);
    columnCount = Math.max(columnCount, shown.length);
    rows.push({ rowNumber: row.rowNumber, cells: shown });
  }
  return { name, hidden, rows, columnCount, totalRows };
}

export function csvToSheet(text: string, name = 'CSV'): SpreadsheetSheet {
  return buildSheet(
    name,
    false,
    parseCsv(text).map((cells, i) => ({ rowNumber: i + 1, cells })),
  );
}

function pad2(n: number): string {
  return String(n).padStart(2, '0');
}

const numberFormat = new Intl.NumberFormat('de-DE', { maximumFractionDigits: 10 });

/** Wie Excel es anzeigt, so weit ohne Zahlenformat möglich: deutsche Zahlen und Daten. */
export function formatSpreadsheetCell(value: CellValue): string {
  if (value == null) return '';
  if (value instanceof Date) {
    if (Number.isNaN(value.getTime())) return '';
    const time = `${pad2(value.getUTCHours())}:${pad2(value.getUTCMinutes())}`;
    if (value.getUTCFullYear() < 1900) return time;
    const day = `${pad2(value.getUTCDate())}.${pad2(value.getUTCMonth() + 1)}.${value.getUTCFullYear()}`;
    return time === '00:00' ? day : `${day} ${time}`;
  }
  if (typeof value === 'number') return Number.isFinite(value) ? numberFormat.format(value) : '';
  if (typeof value === 'boolean') return value ? 'WAHR' : 'FALSCH';
  if (typeof value === 'string') return value;
  if (typeof value === 'object') {
    if ('richText' in value && Array.isArray(value.richText)) return value.richText.map((r) => r.text).join('');
    if ('formula' in value || 'sharedFormula' in value) {
      return formatSpreadsheetCell((value as { result?: CellValue }).result ?? null);
    }
    if ('hyperlink' in value) {
      const text = formatSpreadsheetCell((value as { text?: CellValue }).text ?? null);
      return text || String(value.hyperlink ?? '');
    }
    if ('error' in value) return String(value.error ?? '');
  }
  return '';
}

export async function xlsxToSheets(bytes: ArrayBuffer): Promise<SpreadsheetSheet[]> {
  const { default: ExcelJS } = await import('exceljs');
  const workbook = new ExcelJS.Workbook();
  await workbook.xlsx.load(bytes);
  return workbook.worksheets.map((sheet) => {
    const source: Array<{ rowNumber: number; cells: string[] }> = [];
    sheet.eachRow({ includeEmpty: false }, (row, rowNumber) => {
      const cells: string[] = [];
      const count = Math.min(row.cellCount, SPREADSHEET_PREVIEW_MAX_COLS);
      for (let col = 1; col <= count; col += 1) cells.push(formatSpreadsheetCell(row.getCell(col).value));
      source.push({ rowNumber, cells });
    });
    return buildSheet(sheet.name, Boolean(sheet.state && sheet.state !== 'visible'), source);
  });
}

export async function loadSpreadsheetPreview(
  url: string,
  kind: SpreadsheetPreviewKind,
  filename: string,
): Promise<SpreadsheetSheet[]> {
  // same-origin: Cookies nur an die eigene API, signierte Storage-URLs ohne Credentials (CORS).
  const resp = await fetch(url, { credentials: 'same-origin' });
  if (!resp.ok) throw new Error(`Datei konnte nicht geladen werden (${resp.status}).`);
  const bytes = await resp.arrayBuffer();
  if (kind === 'csv') return [csvToSheet(decodeCsvBytes(bytes), filename.replace(/\.csv$/i, '') || 'CSV')];
  try {
    return await xlsxToSheets(bytes);
  } catch {
    throw new Error('Excel-Datei konnte nicht gelesen werden.');
  }
}
